import { useState } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Link, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Coins, Loader2,
  ShieldCheck, Zap, MessageCircle, Clock,
} from "lucide-react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardTopNav } from "@/components/layout/DashboardTopNav";
import { TeacherSidebar } from "@/components/teacher/dashboard/TeacherSidebar";
import { useTeacherProfile } from "@/contexts/TeacherProfileContext";
import { useAuth } from "@/hooks/use-auth-compat";
import { fetchPublicSubscriptionPlans } from "@/services/teacherSubscriptionService";
import { fetchCoinPackages, type CoinPackage } from "@/services/coinPurchaseService";
import { usePurchaseCoins } from "@/hooks/use-coin-purchase";
import { currencySymbol } from "@/lib/currency";
import { formatPackMinor, packAmountMinorFor, toPurchaseCurrency } from "@/lib/paymentAmounts";
import { PricesRetryAlert } from "@/components/coins/PricesRetryAlert";
import { cn } from "@/lib/utils";

export default function TeacherBuyCoinsPage() {
  usePageTitle('Buy BMC Coins | BookMyCoaching');
  const { profileResponse, teacherProfile, hasActiveVersion, isLoading: profileLoading, refreshProfile } = useTeacherProfile();
  const { user } = useAuth();
  // Teacher PAYMENT currency, resolved server-side -- the same source /pricing and the ad-boost
  // packages endpoint use (CurrencyResolutionService.resolveTeacherPaymentCurrency), so the three
  // can never disagree about what a teacher is quoted.
  //
  // This page previously took its currency from /marketplace/geo-defaults, which is a SEARCH
  // default built for students (it honours a student's persisted profile currency, ADR-067) and
  // falls back to DEFAULT_CURRENCY = "USD". The teacher payment resolver falls back to
  // TEACHER_DEFAULT_CURRENCY = "INR". A teacher whose country could not be resolved therefore saw
  // $3.99 here and the equivalent of ₹299 on /pricing and the ad-boost card, for the same pack.
  //
  // That was not cosmetic: POST /coins/purchase-orders prices the order from the currency the
  // CLIENT sends, so the displayed currency is the one charged.
  const {
    data: plansForCurrency,
    isError: plansError,
    refetch: refetchPlans,
  } = useQuery({
    queryKey: ["public", "teacher-plans"],
    queryFn: fetchPublicSubscriptionPlans,
    staleTime: 5 * 60 * 1000,
  });
  // SCRUM-365: the plans currency exactly as returned -- no INR fallback (D2).
  const rawCurrency = plansForCurrency?.currency ?? null;
  const sym = currencySymbol(rawCurrency);
  const showPlansError = plansError;

  const { data: packages, isLoading } = useQuery({
    queryKey: ["coinPackages"],
    queryFn: fetchCoinPackages,
    staleTime: 5 * 60 * 1000,
  });

  const purchase = usePurchaseCoins(() => refreshProfile(true));
  const balance = profileResponse?.profile?.coinBalance ?? 0;
  const teacherName = profileResponse?.profile?.fullName ?? user?.profile?.name ?? "";
  const teacherAvatar = teacherProfile?.profileImage ?? "";

  const priceOf = (pkg: CoinPackage) => (packAmountMinorFor(pkg, rawCurrency) ?? 0) / 100;
  // A pack has a price only when the backend currency is INR or USD (D2).
  const priceKnown = (pkg: CoinPackage) => packAmountMinorFor(pkg, rawCurrency) !== null;
  const purchaseCurrency = toPurchaseCurrency(rawCurrency);
  const fmt = (n: number) =>
    `${sym}${Number.isInteger(n) ? n.toLocaleString() : n.toFixed(2)}`;

  const defaultPkg = packages?.find((p) => p.popular) ?? packages?.[0];
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const selected = packages?.find((p) => p.code === selectedCode) ?? defaultPkg;

  const selectedPrice = selected ? priceOf(selected) : 0;
  const selectedReplies = selected ? Math.round(selected.coins / 30) : 0;
  const balanceAfter = (balance ?? 0) + (selected?.coins ?? 0);
  const perCoinRate = selected ? selectedPrice / selected.coins : 0;
  const basePkg = packages?.[0];
  const basePricePerCoin = basePkg ? priceOf(basePkg) / basePkg.coins : 0;
  const savings =
    selected && basePricePerCoin > 0
      ? (basePricePerCoin - perCoinRate) * selected.coins
      : 0;

  const isPending =
    purchase.isPending && purchase.variables?.packageCode === selected?.code;

  if (!profileLoading && !hasActiveVersion) {
    return <Navigate to="/teacher-coins" replace />;
  }

  return (
    <SidebarProvider>
      <DashboardTopNav role="teacher" />
      <div className="min-h-screen flex w-full pt-14">
        <TeacherSidebar teacherName={teacherName} teacherAvatar={teacherAvatar} />
        <main className="flex-1 overflow-auto bg-slate-50">

          {/* ── Header ── */}
          <div className="px-4 sm:px-6 pt-5 pb-4 border-b border-slate-100">
            <Link
              to="/teacher-coins"
              className="inline-flex items-center gap-1 text-sm font-medium text-slate-400 hover:text-slate-700 transition-colors mb-3"
            >
              <ArrowLeft className="w-4 h-4" /> My Coins
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 mb-1">Buy BMC Coins</h1>
            <p className="text-sm text-slate-500 mb-3">
              Each student reply costs <span className="font-semibold text-slate-700">30 coins</span> · Buy once, use anytime · Never expire
            </p>
            <div className="flex items-center gap-2.5 flex-wrap">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 ring-1 ring-indigo-100 text-indigo-700 text-xs font-semibold px-3 py-1.5">
                <Coins className="w-3.5 h-3.5" />
                {balance > 0 ? `${balance.toLocaleString()} coins available` : "0 coins — start now"}
              </span>
            </div>
          </div>

          <div className="px-4 sm:px-6 py-5">
            {showPlansError && <PricesRetryAlert onRetry={() => void refetchPlans()} className="mb-5" />}
            {isLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-40 rounded-2xl bg-slate-100 animate-pulse" />
                ))}
              </div>
            ) : (
              <>
                {/* ── Package tiles ── */}
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">
                  Choose a package
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                  {(packages ?? []).map((pkg) => {
                    const pay = priceOf(pkg);
                    const face = pkg.discountPct > 0 ? pay / (1 - pkg.discountPct / 100) : pay;
                    const replies = Math.round(pkg.coins / 30);
                    const isSelected = pkg.code === (selected?.code ?? null);
                    return (
                      <button
                        key={pkg.code}
                        onClick={() => setSelectedCode(pkg.code)}
                        className={cn(
                          "relative text-left rounded-2xl p-4 flex flex-col gap-1 transition-all duration-200 focus:outline-none cursor-pointer border-2",
                          isSelected
                            ? "bg-indigo-50 border-indigo-500 ring-2 ring-indigo-500/20 shadow-sm"
                            : "bg-white border-slate-200 hover:border-indigo-300 hover:shadow-sm",
                        )}
                      >
                        {pkg.popular && (
                          <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-0.5 text-xs font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap shadow-sm bg-indigo-600 text-white">
                            <Zap className="w-2.5 h-2.5" /> Most popular
                          </span>
                        )}
                        {pkg.discountPct > 0 && (
                          <span className="absolute top-2.5 right-2.5 text-xs font-black px-1.5 py-0.5 rounded-md leading-tight bg-rose-500 text-white">
                            -{pkg.discountPct}%
                          </span>
                        )}

                        <p className="text-3xl font-black leading-none mt-1 text-slate-900">
                          {pkg.coins.toLocaleString()}
                        </p>
                        <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
                          BMC Coins
                        </p>
                        <p className="text-xs font-semibold mt-1.5 flex items-center gap-1 text-indigo-600">
                          <MessageCircle className="w-3 h-3 shrink-0" />
                          {replies} student {replies === 1 ? "reply" : "replies"}
                        </p>
                        <div className="flex items-baseline gap-1.5 mt-2 flex-wrap">
                          {priceKnown(pkg) ? (
                            <span className="text-sm font-extrabold text-slate-800">
                              {formatPackMinor(packAmountMinorFor(pkg, rawCurrency), rawCurrency)}
                            </span>
                          ) : (
                            <span className="text-sm font-extrabold text-slate-800" aria-label="Price unavailable">
                              {formatPackMinor(null, rawCurrency)}
                            </span>
                          )}
                          {pkg.discountPct > 0 && priceKnown(pkg) && (
                            <span className="text-xs line-through text-slate-300">{fmt(face)}</span>
                          )}
                        </div>

                        {isSelected && (
                          <CheckCircle2 className="absolute bottom-3 right-3 w-4 h-4 text-indigo-500" />
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* ── What you get + CTA ── */}
                {selected && !showPlansError && (
                  <div className="rounded-2xl overflow-hidden shadow-sm">
                    {/* Stats mosaic */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-slate-100">
                      <div className="bg-white px-4 py-4">
                        <p className="text-xs text-slate-400 font-medium mb-1">Student replies</p>
                        <p className="text-2xl font-black text-slate-900 leading-none">{selectedReplies}</p>
                        <p className="text-xs text-slate-400 mt-1">@ 30 coins each</p>
                      </div>
                      <div className="bg-white px-4 py-4">
                        <p className="text-xs text-slate-400 font-medium mb-1">Balance after</p>
                        <p className="text-2xl font-black text-indigo-600 leading-none">{balanceAfter.toLocaleString()}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {balance > 0 ? `${balance} + ${selected.coins.toLocaleString()}` : "coins total"}
                        </p>
                      </div>
                      <div className="bg-white px-4 py-4">
                        <p className="text-xs text-slate-400 font-medium mb-1">Rate per coin</p>
                        <p className="text-2xl font-black text-slate-900 leading-none">
                          {!priceKnown(selected)
                            ? "—"
                            : <>{sym}{perCoinRate < 1 ? perCoinRate.toFixed(2) : perCoinRate.toFixed(1)}</>}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">per BMC Coin</p>
                      </div>
                      <div className="bg-white px-4 py-4">
                        <p className="text-xs text-slate-400 font-medium mb-1">You save</p>
                        <p className={cn("text-2xl font-black leading-none", savings > 0 ? "text-emerald-600" : "text-slate-200")}>
                          {savings > 0 ? fmt(Math.round(savings)) : "—"}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">vs smallest pack</p>
                      </div>
                    </div>

                    {/* CTA */}
                    <div className="bg-white px-4 py-4 flex flex-col gap-3">
                      <button
                        onClick={() => {
                          // The server-returned plans currency (D11); null means Pay is disabled.
                          if (!purchaseCurrency) return;
                          purchase.mutate({ packageCode: selected.code, currency: purchaseCurrency, buyerRole: "TEACHER" });
                        }}
                        disabled={purchase.isPending || !priceKnown(selected) || !purchaseCurrency}
                        className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 active:scale-[0.99] text-white text-base font-bold px-6 py-3.5 rounded-xl transition-all shadow-sm hover:shadow-md disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {isPending ? (
                          <><Loader2 className="w-4 h-4 animate-spin" /> Processing…</>
                        ) : (
                          <>
                            Pay {priceKnown(selected) ? fmt(selectedPrice) : "—"} · Get {selected.coins.toLocaleString()} coins
                            <ArrowRight className="w-4 h-4" />
                          </>
                        )}
                      </button>

                      {/* Trust signals */}
                      <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5">
                        <span className="flex items-center gap-1.5 text-xs text-slate-400">
                          <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> Secured by Razorpay
                        </span>
                        <span className="flex items-center gap-1.5 text-xs text-slate-400">
                          <Coins className="w-3.5 h-3.5 text-indigo-400" /> Coins never expire
                        </span>
                        <span className="flex items-center gap-1.5 text-xs text-slate-400">
                          <Clock className="w-3.5 h-3.5 text-amber-400" /> Instant credit
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            <p className="text-xs text-slate-400 mt-4">
              Questions?{" "}
              <Link to="/pricing" className="text-indigo-600 hover:text-indigo-700 font-semibold">
                Full pricing &amp; FAQ
              </Link>
            </p>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
